import {useEffect, useRef, useState} from "react";
import {
    Alert,
    Button,
    Grid,
    Icon,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import {useDialog} from "muibox";
import useFeedback from "../../components/feedback/useFeedback";
import {
    consultarIngressoEstatutario,
    usePrepararIngressoEstatutario,
    usePrepararConvocacaoConcurso
} from "../../api/DpgeIntegracaoEsfingeApi";
import OcorrenciasGeradas from "./OcorrenciasGeradas";

const campos = {
    numero: "Número do ato",
    tipoAto: "Tipo do ato (Tabela 47)",
    dataAto: "Data do ato",
    dataInicioVigencia: "Início da vigência do ato",
    dataPublicacao: "Data da publicação",
    numeroMatricula: "Matrícula",
    tipoQuadro: "Quadro de pessoal",
    codigoCargo: "Código do cargo",
    dataInicioVigenciaCargo: "Início da vigência do cargo",
    descricaoLotacao: "Lotação",
    numeroEdital: "Número do edital",
    numeroInscricaoAprovado: "Inscrição do aprovado",
    dataPosse: "Data da posse",
    dataEfetivoExercicio: "Data do exercício",
    descricaoHabilitacao: "Habilitação",
    tipoEscolaridade: "Escolaridade (Tabela 97)",
    dataLaudoSaude: "Data do laudo de saúde",
    indicativoAptidao: "Apto no laudo de saúde?",
    indicativoAcumulacaoCargo: "Acumula cargo?",
    nomeCargoAcumulado: "Cargo acumulado",
    codigoEsferaCargoAcumulado: "Esfera do cargo acumulado (Tabela 60)",
    tipoCargaHoraria: "Tipo da carga horária",
    qtdCargaHoraria: "Carga horária (hhhmm)",
    indicativoQuitacaoEleitoral: "Possui quitação eleitoral?",
    dataDeclaracaoBens: "Data da declaração de bens",
    indicativoPenalidadeDisciplinar: "Possui penalidade disciplinar?",
    numeroDecisaoJudicial: "Número da decisão judicial",
    dataDecisaoJudicial: "Data da decisão judicial"
};
export function referenciaCompetencia(anoMes) {
    if (
        !/^\d{6}$/.test(anoMes) ||
        +anoMes.slice(4) < 1 ||
        +anoMes.slice(4) > 12
    )
        return "";
    const dia = new Date(
        Date.UTC(+anoMes.slice(0, 4), +anoMes.slice(4), 0)
    ).getUTCDate();
    return `${anoMes.slice(0, 4)}-${anoMes.slice(4)}-${dia}`;
}

export default function IngressoEstatutarioPanel({anoMes}) {
    const [movimento, setMovimento] = useState("");
    const [diagnostico, setDiagnostico] = useState(null);
    const [ocorrencias, setOcorrencias] = useState([]);
    const [ocupado, setOcupado] = useState(false);
    const lock = useRef(false);
    const ativo = useRef(true);
    useEffect(() => {
        ativo.current = true;
        return () => {
            ativo.current = false;
        };
    }, []);
    const dialog = useDialog();
    const {showError} = useFeedback();
    const ingresso = usePrepararIngressoEstatutario();
    const convocar = usePrepararConvocacaoConcurso();
    const referencia = referenciaCompetencia(anoMes);
    const origem = diagnostico?.dadosDaFolha || {};

    async function executar(operacao, titulo) {
        if (lock.current) return;
        lock.current = true;
        setOcupado(true);
        try {
            if (titulo) {
                try {
                    await dialog.confirm({
                        title: titulo,
                        ok: {text: "CONFIRMAR", color: "primary"},
                        cancel: {text: "CANCELAR", color: "primary"}
                    });
                } catch (e) {
                    return;
                }
            }
            if (!ativo.current) return;
            await operacao();
        } catch (erro) {
            if (ativo.current) showError(erro);
        } finally {
            lock.current = false;
            if (ativo.current) setOcupado(false);
        }
    }

    function limpar() {
        setDiagnostico(null);
        setOcorrencias([]);
    }
    async function consultar() {
        limpar();
        const resposta = await consultarIngressoEstatutario(
            movimento.trim(),
            referencia
        );
        if (ativo.current) setDiagnostico(resposta.data);
    }
    async function preparar(isConvocacao) {
        const resposta = await (isConvocacao ? convocar : ingresso).mutateAsync(
            {
                movimento: diagnostico.movimento,
                referencia
            }
        );
        if (ativo.current)
            setOcorrencias((anteriores) => [
                ...anteriores.filter((o) => o.id !== resposta.data.id),
                resposta.data
            ]);
    }

    return (
        <Stack spacing={2}>
            <Alert severity="info">
                Efetivos: resultado do concurso → convocação aceita → ingresso
                estatutário aceito → vínculos e movimentos. Comissionados
                estatutários: ingresso aceito → vínculos e movimentos. Cargo e
                identificação devem estar aceitos. Registros já aceitos são
                preservados.
            </Alert>
            <TextField
                label="Movimento de origem na Folha"
                helperText="Informe I-id para admissão ou C-id-I para nomeação em comissão. Use o identificador do movimento funcional da Folha, não o número da ocorrência."
                value={movimento}
                disabled={ocupado}
                onChange={(e) => {
                    setMovimento(e.target.value);
                    limpar();
                }}
                fullWidth
            />
            <Button
                variant="outlined"
                startIcon={<Icon>search</Icon>}
                disabled={
                    ocupado ||
                    !referencia ||
                    !/^(I-[1-9]\d*|C-[1-9]\d*-I)$/.test(movimento.trim())
                }
                onClick={() => executar(consultar)}
            >
                Conferir dados do ingresso
            </Button>
            {diagnostico && (
                <>
                    <Typography variant="subtitle2">
                        {diagnostico.cargoEfetivo
                            ? "Nomeação efetiva"
                            : "Nomeação em comissão"}{" "}
                        — referência {referencia.split("-").reverse().join("/")}
                    </Typography>
                    {!!diagnostico.pendencias?.length && (
                        <Alert severity="warning">
                            <Typography variant="subtitle2">
                                Pendências da origem
                            </Typography>
                            <ul>
                                {diagnostico.pendencias.map((p) => (
                                    <li key={p}>
                                        {p.replace(
                                            /^[^:]+/,
                                            (campo) => campos[campo] || campo
                                        )}
                                    </li>
                                ))}
                            </ul>
                            Corrija os dados na Folha, em Dados Funcionais ou na
                            nomeação correspondente da aba Cargos Comissão, e
                            confira novamente. As dependências serão verificadas
                            na preparação.
                        </Alert>
                    )}
                    {diagnostico.cargoEfetivo && (
                        <Stack spacing={2}>
                            <Typography variant="subtitle2">
                                4.2. Convocação do concurso
                            </Typography>
                            {Object.entries({
                                dataConvocacao: "Data da convocação",
                                tipoConvocacao: "Tipo de convocação",
                                ordemClassificacao: "Ordem da convocação"
                            }).map(([campo, label]) => (
                                <TextField
                                    key={campo}
                                    fullWidth
                                    label={label}
                                    value={
                                        diagnostico.convocacaoDaFolha?.[
                                            campo
                                        ] || ""
                                    }
                                    InputProps={{readOnly: true}}
                                    helperText="Cadastro da Folha — Dados Funcionais / Convocação"
                                />
                            ))}
                            {!!diagnostico.pendenciasConvocacao?.length && (
                                <Alert severity="warning">
                                    Complete a convocação em Dados Funcionais na
                                    Folha.
                                    <ul>
                                        {diagnostico.pendenciasConvocacao.map(
                                            (p) => (
                                                <li key={p}>{p}</li>
                                            )
                                        )}
                                    </ul>
                                </Alert>
                            )}
                            <Button
                                variant="outlined"
                                startIcon={<Icon>playlist_add</Icon>}
                                disabled={
                                    ocupado ||
                                    !diagnostico.pendenciasConvocacao ||
                                    diagnostico.pendenciasConvocacao.length > 0
                                }
                                onClick={() =>
                                    executar(
                                        () => preparar(true),
                                        "Deseja preparar a convocação para revisão e aprovação manual?"
                                    )
                                }
                            >
                                Preparar convocação
                            </Button>
                        </Stack>
                    )}
                    <Typography variant="subtitle2">
                        4.3. Ingresso estatutário
                    </Typography>
                    <Typography variant="body2">
                        O cadastro e as declarações são mantidos na Folha. O
                        conector consulta os dados e prepara a ocorrência para
                        aprovação.
                    </Typography>
                    <Grid container spacing={2}>
                        {Object.entries(campos)
                            .filter(
                                ([campo]) =>
                                    diagnostico.cargoEfetivo ||
                                    ![
                                        "numeroEdital",
                                        "numeroInscricaoAprovado",
                                        "descricaoHabilitacao"
                                    ].includes(campo)
                            )
                            .map(([campo, label]) => (
                                <Grid item xs={12} sm={6} key={campo}>
                                    <TextField
                                        fullWidth
                                        label={label}
                                        value={origem[campo] || ""}
                                        InputProps={{readOnly: true}}
                                        InputLabelProps={{shrink: true}}
                                        helperText={
                                            origem[campo]
                                                ? "Informado pela Folha"
                                                : "Não informado na Folha"
                                        }
                                    />
                                </Grid>
                            ))}
                    </Grid>
                    <Button
                        variant="contained"
                        startIcon={<Icon>playlist_add</Icon>}
                        disabled={
                            ocupado ||
                            !diagnostico.pendencias ||
                            diagnostico.pendencias.length > 0
                        }
                        onClick={() =>
                            executar(
                                () => preparar(false),
                                "Deseja preparar o ingresso estatutário com os dados conferidos para revisão e aprovação manual?"
                            )
                        }
                    >
                        Preparar ingresso estatutário
                    </Button>
                </>
            )}
            {ocorrencias.length > 0 && (
                <Alert severity="info">
                    A preparação não autoriza nem transmite. Abra a ocorrência,
                    revise e autorize no fluxo habitual. Aguarde o aceite antes
                    da etapa seguinte.
                </Alert>
            )}
            <OcorrenciasGeradas ocorrencias={ocorrencias} />
        </Stack>
    );
}
